import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  effect,
  HostListener,
  OnInit,
  computed,
  DestroyRef,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FilterPanelComponent } from '../../shared/components/filter-panel/filter-panel.component';
import { ProductCardComponent } from '../../shared/components/product-card/product-card.component';
import { ListToolbarComponent } from '../../shared/components/list-toolbar/list-toolbar.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { PublicListingService } from '../../core/services/listing/public-listing.service';
import { FilterConfig, FilterState } from '../../shared/models/filter.model';
import { AppMainService } from '../../core/services/core/app-main.service';
import { AppMaster } from '../../core/models/api/app-main.model';

@Component({
  selector: 'app-search-merchandises',
  standalone: true,
  imports: [
    CommonModule,
    FilterPanelComponent,
    ProductCardComponent,
    ListToolbarComponent,
    PaginationComponent,
  ],
  templateUrl: './search-merchandises.component.html',
  styleUrls: ['./search-merchandises.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchMerchandisesComponent implements OnInit {
  private productService = inject(PublicListingService);
  private router = inject(Router);
  private appMainService = inject(AppMainService);

  private platformId = inject(PLATFORM_ID);

  readonly pageSize = 10;
  readonly initialPage = 1;

  // Signals for View State
  viewMode = signal<'grid' | 'list'>(this.getInitialViewMode());
  currentSort = signal<string>(this.getInitialSort());
  currentPage = signal<number>(this.getInitialPage());

  private getInitialViewMode(): 'grid' | 'list' {
    if (isPlatformBrowser(this.platformId)) {
      return (localStorage.getItem('seger_merch_view') as any) || 'grid';
    }
    return 'grid';
  }

  private getInitialSort(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('seger_merch_sort') || 'newest';
    }
    return 'newest';
  }

  private getInitialPage(): number {
    if (isPlatformBrowser(this.platformId)) {
      return parseInt(localStorage.getItem('seger_merch_page') || '1', 10);
    }
    return 1;
  }

  // Mobile Filter Drawer State
  isFilterDrawerOpen = signal<boolean>(false);

  // Initial Loading State
  isLoading = signal<boolean>(false);

  private destroyRef = inject(DestroyRef);

  // Filter Config for Merchandises (Dynamic)
  filterConfig: FilterConfig[] = [];

  activeFilters = signal<FilterState>(this.loadFiltersFromStorage());
  allMerchsRaw = signal<any[]>([]);

  private loadFiltersFromStorage(): FilterState {
    const defaultState = {
      price: { min: 0, max: 500 },
    };
    try {
      if (isPlatformBrowser(this.platformId)) {
        const stored = localStorage.getItem('seger_merch_filters');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Ensure price is valid and not stuck at 0-0
          if (parsed.price && parsed.price.max === 0) {
            delete parsed.price;
          }
          return { ...defaultState, ...parsed };
        }
      }
    } catch {}
    return defaultState;
  }

  ngOnInit() {
    this.loadMasterData();
    this.loadProducts();
  }

  private loadProducts() {
    this.isLoading.set(true);
    this.productService
      .getCachedMerchs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.allMerchsRaw.set(data);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('[SearchMerchandises] Error loading products:', err);
          this.isLoading.set(false);
        },
      });
  }

  private loadMasterData() {
    this.appMainService.getMasterConfig().subscribe({
      next: (master: AppMaster) => {
        this.initializeFilters(master);
      },
    });
  }

  private initializeFilters(master: any) {
    const configs: FilterConfig[] = [];

    // 1. Group Merchandise Features by Type (e.g., Size, Color, Material)
    const features = master.merchandiseFeatures || [];
    const groups: { [key: string]: any[] } = {};

    features.forEach((f: any) => {
      const type = f.featureType || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push({
        label: f.featureName,
        value: f.featureId.toString(),
      });
    });

    // 2. Add groups to config
    Object.keys(groups).forEach((type) => {
      configs.push({
        key: `feature_${type.toLowerCase().replace(/\s+/g, '_')}`,
        label: type,
        type: 'checkbox',
        options: groups[type],
        collapsed: false,
      });
    });

    // 3. Add Price Range
    configs.push({
      key: 'price',
      label: 'Price Range',
      type: 'range',
      min: 0,
      max: 500,
      unit: '$',
      step: 1,
      collapsed: false,
    });

    // 4. Add Rating filter at the very bottom
    configs.push({
      key: 'rating',
      label: 'Rating',
      type: 'rating',
      options: [],
      collapsed: false,
    });

    this.filterConfig = configs;
  }

  // Constructor with effect to sync URL → Component state
  constructor() {
    // Safety sync: If switching from yachts, clear locationId as merchandise don't use it
    effect(
      () => {
        const current = this.activeFilters();
        if (current['locationId']) {
          const updated = { ...current };
          delete updated['locationId'];
          this.activeFilters.set(updated);
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('seger_merch_filters', JSON.stringify(updated));
          }
        }
      },
      { allowSignalWrites: true },
    );
  }

  // Computed: Filtered + Sorted list
  filteredAndSorted = computed(() => {
    let items = [...this.allMerchsRaw()];
    const filters = this.activeFilters();
    const sort = this.currentSort();

    // 1. Price Filtering
    if (filters['price']) {
      const price = filters['price'] as { min: number; max: number };
      items = items.filter((m) => {
        const p = m.price ?? 0;
        return p >= (price.min || 0) && p <= (price.max || Infinity);
      });
    }

    // 2. Rating Filtering
    if (filters['rating']) {
      const minRating = filters['rating'] as number;
      items = items.filter((m) => (m.averageRating || 0) >= minRating);
    }

    // 3. Group Merchandise Features
    const selectedFeatureIds: number[] = [];
    Object.keys(filters).forEach((key) => {
      if (key.startsWith('feature_') && Array.isArray(filters[key])) {
        const vals = filters[key].filter((v) => v !== 'all');
        vals.forEach((v) => selectedFeatureIds.push(parseInt(v, 10)));
      }
    });

    if (selectedFeatureIds.length > 0) {
      items = items.filter((m) => {
        const itemFeatures = m.raw?.merchandise?.merchandiseFeatureMappings || [];
        return selectedFeatureIds.every((id) =>
          itemFeatures.some((f: any) => f.merchandiseFeatureId === id),
        );
      });
    }

    // 4. Manual Sorting (Synchronous)
    items.sort((a, b) => {
      const getPrice = (item: any) => item.price ?? 0;
      const getDate = (item: any) => (item.updatedDate ? new Date(item.updatedDate).getTime() : 0);

      if (sort === 'price_asc') return getPrice(a) - getPrice(b);
      if (sort === 'price_desc') return getPrice(b) - getPrice(a);
      if (sort === 'newest') return getDate(b) - getDate(a);
      return (b.id || 0) - (a.id || 0);
    });

    return items;
  });

  // Final Derived Signals for Template
  totalItems = computed(() => this.filteredAndSorted().length);

  products = computed(() => {
    const items = this.filteredAndSorted();
    const start = (this.currentPage() - 1) * this.pageSize;
    const paginated = items.slice(start, start + this.pageSize);

    return paginated.map((dto: any) => {
      return (this.productService as any).toProductDetail(dto, true);
    });
  });

  // Event Handlers
  onFilterChange(filters: FilterState) {
    this.activeFilters.set(filters);
    this.currentPage.set(1);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_merch_filters', JSON.stringify(filters));
      localStorage.setItem('seger_merch_page', '1');
    }
  }

  onSortChange(sort: string) {
    this.currentSort.set(sort);
    this.currentPage.set(1);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_merch_sort', sort);
      localStorage.setItem('seger_merch_page', '1');
    }
  }

  onViewChange(mode: 'grid' | 'list') {
    this.viewMode.set(mode);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_merch_view', mode);
    }
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_merch_page', page.toString());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onSourceChange(source: 'yachts' | 'merchandises') {
    if (source === 'yachts') {
      this.router.navigate(['/yachts']);
    }
  }

  // Mobile Filter Drawer Handlers
  onFilterToggle() {
    this.isFilterDrawerOpen.set(!this.isFilterDrawerOpen());
    this.toggleBodyScroll();
  }

  closeFilterDrawer() {
    this.isFilterDrawerOpen.set(false);
    this.enableBodyScroll();
  }

  onDrawerBackdropClick() {
    this.closeFilterDrawer();
  }

  // Clear All Filters
  clearAllFilters() {
    const defaultState = {
      price: { min: 0, max: 500 },
    };
    this.activeFilters.set(defaultState);
    this.currentPage.set(1);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_merch_filters', JSON.stringify(defaultState));
    }
    this.closeFilterDrawer();
  }

  // ESC Key Handler
  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isFilterDrawerOpen()) {
      this.closeFilterDrawer();
    }
  }

  // Body Scroll Lock Management
  private toggleBodyScroll() {
    // if (this.isFilterDrawerOpen()) {
    //     document.body.style.overflow = 'hidden';
    // } else {
    //     document.body.style.overflow = '';
    // }
  }

  private enableBodyScroll() {
    // document.body.style.overflow = '';
  }
}
